import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { RootState } from "@/redux/store"
import { LoginPath } from "@/router"
import { getToken, removeToken } from "@/helpers/storage"
import { clearMe } from "@/redux/actions"
import store from "@/redux/store"

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const { me, initialized } = useSelector((state: RootState) => state.me)

  const isAuthenticated = !!me && !!getToken()

  const logout = () => {
    removeToken()
    store.dispatch(clearMe())
    navigate(LoginPath)
  }

  // Verificar token al cargar la aplicación
  useEffect(() => {
    const checkAuth = () => {
      const token = getToken()

      if (!token) {
        // No hay token, redirigir al login si no estamos ya ahí
        const currentPath = window.location.pathname
        if (
          !currentPath.includes("/login") &&
          !currentPath.includes("/logout")
        ) {
          navigate(LoginPath)
        }
        setIsLoading(false)
        return
      }

      // Verificar si el token ha expirado (asume formato JWT)
      try {
        const decoded = JSON.parse(atob(token.split(".")[1]))
        const expirationTime = new Date(decoded.exp * 1000)
        const currentTime = new Date()

        if (expirationTime <= currentTime) {
          logout()
          return
        }
      } catch {
        logout()
        return
      }

      setIsLoading(false)
    }

    checkAuth()
  }, [navigate])

  // Verificar autenticación cuando cambia el estado del usuario
  useEffect(() => {
    if (initialized && !isAuthenticated) {
      const currentPath = window.location.pathname
      if (!currentPath.includes("/login") && !currentPath.includes("/logout")) {
        navigate(LoginPath)
      }
    }
  }, [isAuthenticated, initialized, navigate])

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
